import { useCallback, useRef } from "react";
import { withArtikelImage } from "@/components/ArtikelImage";
import { Autocomplete, type AutocompleteOption } from "@/components/Autocomplete";
import { searchMaterials, type MaterialSuggestion } from "@/lib/wartungen";
import {
  getAllowFreeInput,
  getSelectOptions,
  isCheckedAnswer,
  isValueEmpty,
  type NormalizedField,
} from "./runtimeModel";

interface RuntimeFieldProps {
  field: NormalizedField;
  value: unknown;
  error?: string;
  readOnly?: boolean;
  onChange: (value: unknown) => void;
}

const FREE_INPUT_SENTINEL = "__eh_free__";

export function RuntimeField({ field, value, error, readOnly = false, onChange }: RuntimeFieldProps) {
  const type = field.effective_field_type;
  const disabled = readOnly || !field.resolved_enabled;
  const required = field.resolved_required;

  if (type === "divider") return <hr className="my-4 border-separator" />;
  if (type === "static_text") {
    return <p className="text-sm text-label-secondary">{field.effective_label}</p>;
  }

  const labelNode = (
    <span className="eh-label text-xs">
      {field.effective_label}
      {required ? <span className="ml-1 text-status-red">*</span> : null}
    </span>
  );

  function wrap(input: React.ReactNode, full = false) {
    return (
      <label className={`space-y-1 ${full ? "sm:col-span-2" : ""}`}>
        {labelNode}
        {input}
        {error ? <span className="block text-xs text-status-red">{error}</span> : null}
      </label>
    );
  }

  switch (type) {
    case "number":
      return wrap(
        <input
          type="number"
          className="eh-input w-full"
          disabled={disabled}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />,
      );
    case "date":
      return wrap(
        <input
          type="date"
          className="eh-input w-full"
          disabled={disabled}
          value={value ? String(value).slice(0, 10) : ""}
          onChange={(e) => onChange(e.target.value || null)}
        />,
      );
    case "datetime":
      return wrap(
        <input
          type="datetime-local"
          className="eh-input w-full"
          disabled={disabled}
          value={value ? String(value).slice(0, 16) : ""}
          onChange={(e) => onChange(e.target.value || null)}
        />,
      );
    case "bool":
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm text-label">
          <input
            type="checkbox"
            disabled={disabled}
            checked={isCheckedAnswer(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
          {field.effective_label}
          {required ? <span className="text-status-red">*</span> : null}
          {error ? <span className="ml-2 text-xs text-status-red">{error}</span> : null}
        </label>
      );
    case "select":
      return wrap(<SelectField field={field} value={value} disabled={disabled} onChange={onChange} />);
    case "multiselect":
      return wrap(<MultiSelectField field={field} value={value} disabled={disabled} onChange={onChange} />, true);
    case "textarea":
      return wrap(
        <textarea
          className="eh-input w-full"
          rows={3}
          disabled={disabled}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />,
        true,
      );
    case "formula":
      return wrap(
        <input className="eh-input w-full bg-fill-tertiary" disabled value={value == null ? "" : String(value)} readOnly />,
      );
    case "geo":
      return wrap(<GeoField value={value} disabled={disabled} onChange={onChange} />, true);
    case "signature":
      return wrap(<SignatureField value={value} disabled={disabled} onChange={onChange} />, true);
    case "file":
    case "photo":
    case "pdf-upload":
      return wrap(<FileField type={type} value={value} disabled={disabled} onChange={onChange} />, true);
    case "article_ref":
      return wrap(<ArticleRefField value={value} disabled={disabled} onChange={onChange} />, true);
    case "material_link":
      return wrap(<MaterialLinkField value={value} disabled={disabled} onChange={onChange} />, true);
    case "text":
    default:
      return wrap(
        <input
          type="text"
          className="eh-input w-full"
          disabled={disabled}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />,
      );
  }
}

function SelectField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: NormalizedField;
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}) {
  const options = getSelectOptions(field);
  const free = getAllowFreeInput(field);
  const stringValue = value == null ? "" : String(value);
  const isFree = free.allow && stringValue !== "" && !options.some((o) => o.value === stringValue);

  return (
    <>
      <select
        className="eh-input w-full"
        disabled={disabled}
        value={isFree ? FREE_INPUT_SENTINEL : stringValue}
        onChange={(e) => {
          if (e.target.value === FREE_INPUT_SENTINEL) onChange("");
          else onChange(e.target.value || null);
        }}
      >
        <option value="">Bitte wählen …</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        {free.allow ? <option value={FREE_INPUT_SENTINEL}>{free.label}</option> : null}
      </select>
      {isFree ? (
        <input
          className="eh-input mt-2 w-full"
          placeholder={free.label}
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : null}
    </>
  );
}

function MultiSelectField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: NormalizedField;
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}) {
  const options = getSelectOptions(field);
  const selected = new Set(Array.isArray(value) ? value.map((v) => String(v)) : []);
  function toggle(optValue: string) {
    const next = new Set(selected);
    if (next.has(optValue)) next.delete(optValue);
    else next.add(optValue);
    onChange(Array.from(next));
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
            selected.has(opt.value) ? "border-accent bg-accent-soft text-accent" : "border-separator text-label"
          }`}
        >
          <input
            type="checkbox"
            disabled={disabled}
            checked={selected.has(opt.value)}
            onChange={() => toggle(opt.value)}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function GeoField({ value, disabled, onChange }: { value: unknown; disabled: boolean; onChange: (v: unknown) => void }) {
  const coords = (value && typeof value === "object" ? value : {}) as { lat?: number; lng?: number };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <input
        type="number"
        step="any"
        className="eh-input w-32"
        placeholder="Breite"
        disabled={disabled}
        value={coords.lat ?? ""}
        onChange={(e) => onChange({ ...coords, lat: e.target.value === "" ? undefined : Number(e.target.value) })}
      />
      <input
        type="number"
        step="any"
        className="eh-input w-32"
        placeholder="Länge"
        disabled={disabled}
        value={coords.lng ?? ""}
        onChange={(e) => onChange({ ...coords, lng: e.target.value === "" ? undefined : Number(e.target.value) })}
      />
      <button
        type="button"
        className="inline-flex h-10 items-center rounded-lg bg-card px-3 text-sm font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
        disabled={disabled}
        onClick={() => {
          if (!navigator.geolocation) return;
          navigator.geolocation.getCurrentPosition((pos) =>
            onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          );
        }}
      >
        Standort verwenden
      </button>
    </div>
  );
}

function SignatureField({ value, disabled, onChange }: { value: unknown; disabled: boolean; onChange: (v: unknown) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  const pointer = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={360}
        height={140}
        className="touch-none rounded-lg border border-separator bg-card"
        onPointerDown={(e) => {
          if (disabled) return;
          drawing.current = true;
          const ctx = canvasRef.current?.getContext("2d");
          const { x, y } = pointer(e);
          ctx?.beginPath();
          ctx?.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current || disabled) return;
          const ctx = canvasRef.current?.getContext("2d");
          const { x, y } = pointer(e);
          ctx?.lineTo(x, y);
          ctx?.stroke();
        }}
        onPointerUp={() => {
          drawing.current = false;
          const data = canvasRef.current?.toDataURL("image/png");
          if (data) onChange(data);
        }}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="text-xs text-accent hover:underline"
          onClick={() => {
            const ctx = canvasRef.current?.getContext("2d");
            if (ctx && canvasRef.current) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            onChange(null);
          }}
        >
          Zurücksetzen
        </button>
        {!isValueEmpty(value) ? <span className="text-xs text-label-secondary">Unterschrift erfasst</span> : null}
      </div>
    </div>
  );
}

function FileField({
  type,
  value,
  disabled,
  onChange,
}: {
  type: string;
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}) {
  const accept = type === "photo" ? "image/*" : type === "pdf-upload" ? "application/pdf" : undefined;
  const stored = value && typeof value === "object" ? (value as { name?: string; dataUrl?: string }) : null;
  return (
    <div className="space-y-2">
      <input
        type="file"
        accept={accept}
        disabled={disabled}
        className="block w-full text-sm text-label-secondary file:mr-3 file:rounded-lg file:border-0 file:bg-fill-tertiary file:px-3 file:py-2 file:text-sm file:text-label"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) {
            onChange(null);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => onChange({ name: file.name, dataUrl: String(reader.result) });
          reader.readAsDataURL(file);
        }}
      />
      {stored?.name ? <span className="text-xs text-label-secondary">{stored.name}</span> : null}
      {type === "photo" && stored?.dataUrl ? (
        <img src={stored.dataUrl} alt={stored.name || "Foto"} className="max-h-40 rounded-lg border border-separator" />
      ) : null}
    </div>
  );
}

function MaterialLinkField({
  value,
  disabled,
  onChange,
}: {
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}) {
  const current =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as { artikel_id?: number; hero_nr?: string; label?: string; qty?: number; unit?: string })
      : {};

  function patch(partial: Record<string, unknown>) {
    const next = { ...current, ...partial };
    const hasContent =
      next.artikel_id != null ||
      String(next.hero_nr || "").trim() ||
      String(next.label || "").trim() ||
      next.qty != null ||
      String(next.unit || "").trim();
    onChange(hasContent ? next : null);
  }

  const linkedHint =
    current.artikel_id != null
      ? `Artikel aus Stammdaten verknüpft${current.label ? `: ${current.label}` : ""}${
          current.hero_nr ? ` (${current.hero_nr})` : ""
        }`
      : null;

  return (
    <div className="space-y-2">
      {linkedHint ? <p className="text-xs text-label-secondary">{linkedHint}</p> : null}
      <input
        className="eh-input w-full"
        disabled={disabled}
        placeholder="Bezeichnung (manuell)"
        value={String(current.label || "")}
        onChange={(e) => patch({ label: e.target.value })}
      />
      <input
        className="eh-input w-full"
        disabled={disabled}
        placeholder="Hero-Nr. (manuell)"
        value={String(current.hero_nr || "")}
        onChange={(e) => patch({ hero_nr: e.target.value })}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="eh-input w-full"
          type="number"
          min={0}
          step={0.01}
          disabled={disabled}
          placeholder="Menge"
          value={current.qty ?? ""}
          onChange={(e) => patch({ qty: e.target.value === "" ? undefined : Number(e.target.value) })}
        />
        <input
          className="eh-input w-full"
          disabled={disabled}
          placeholder="Einheit"
          value={String(current.unit || "")}
          onChange={(e) => patch({ unit: e.target.value })}
        />
      </div>
    </div>
  );
}

function ArticleRefField({ value, disabled, onChange }: { value: unknown; disabled: boolean; onChange: (v: unknown) => void }) {
  const current = value && typeof value === "object" ? (value as { label?: string; hero_nr?: string; artikel_id?: number }) : null;
  const initial = current ? [current.hero_nr, current.label].filter(Boolean).join(" – ") : "";

  const loadOptions = useCallback(async (query: string): Promise<AutocompleteOption<MaterialSuggestion>[]> => {
    if (query.trim().length < 2) return [];
    const items = await searchMaterials(query);
    return items
      .map((entry) => {
        const id = entry.artikel_id ?? entry.id;
        const heroNr = String(entry.hero_nr || "").trim();
        const label = String(entry.label || entry.name || entry.bezeichnung || "").trim();
        return withArtikelImage({
          key: String(id ?? label),
          label: [heroNr, label].filter(Boolean).join(" – ") || String(id ?? ""),
          value: {
            ...entry,
            image_src: typeof entry.image_src === "string" ? entry.image_src : null,
          },
        });
      })
      .filter((opt) => opt.key);
  }, []);

  if (disabled) {
    return <input className="eh-input w-full bg-fill-tertiary" disabled value={initial} readOnly />;
  }

  return (
    <Autocomplete<MaterialSuggestion>
      placeholder="Artikel suchen (Hero-Nr / Bezeichnung) …"
      initialQuery={initial}
      minChars={2}
      loadOptions={loadOptions}
      onSelect={(opt) => {
        if (!opt) {
          onChange(null);
          return;
        }
        const entry = opt.value;
        onChange({
          artikel_id: entry.artikel_id ?? entry.id,
          hero_nr: String(entry.hero_nr || "").trim(),
          label: String(entry.label || entry.name || entry.bezeichnung || "").trim(),
        });
      }}
    />
  );
}
